import { DateTime } from "luxon";
import _ from "underscore";

export enum IAMEntity {
  User = "IAM User",
  Group = "IAM Group",
  Role = "IAM Role",
  Policy = "IAM Policy",
}

export const EntityMAxLength: Record<IAMEntity, number> = {
  [IAMEntity.User]: 64,
  [IAMEntity.Group]: 128,
  [IAMEntity.Role]: 64,
  [IAMEntity.Policy]: 128,
};

class Validators {
  isEmail(email: string): boolean {
    return /^[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+$/.test(email);
  }

  isPassword(password: string): boolean {
    return /^(?=.{8})(?=.*?[a-z])(?=.*?[A-Z])(?=.*?\d)(?=.*?[!@#$%^&*_=+.,/?~]+)/.test(
      password,
    );
  }

  validDate(date: DateTime): string {
    return !date || date.isValid ? null : "Invalid date";
  }

  required(message?: string): (value: any) => string {
    let isEmpty: boolean;
    return (value) => {
      if (_.isString(value)) {
        value = value.trim();
        isEmpty = !value;
      } else if (_.isArray(value)) {
        isEmpty = value.length === 0;
      } else {
        isEmpty = !value;
      }
      return isEmpty ? message || "This field is required" : null;
    };
  }

  validateIAMEntityName(value: string, type: IAMEntity): string | null {
    const error = this.required("Name is required")(value);
    if (error) {
      return error;
    }
    const re = /^[A-Za-z0-9+=,.@_-]+$/;
    if (!re.test(value)) {
      return "Name contains invalid characters";
    }

    if (value.length > EntityMAxLength[type]) {
      return `${type} name must be less than ${EntityMAxLength[type]} characters`;
    }

    return null;
  }

  validateIAMPolicyDescription(value: string): string | null {
    if (value.length > 512) {
      return "Description exceeds the maximum length of 512 characters.";
    }

    const allowedRegex = /^[A-Za-z0-9+=,\.@\-_]+$/;
    if (!allowedRegex.test(value)) {
      return "Description contains invalid characters. Only alphanumeric characters and +=, . @ - _ are allowed.";
    }
  }
  validateIAMPolicyDocument(value: string): string | null {
    return null;
  }

  validateMFAToken(value: string): string {
    return /^\d{6}$/.test(value) ? "" : "Code must be a 6 digit number";
  }

  validateRecoveryCode(value: string): string {
    // code must be [6, 10] bytes alphanumeric string

    return /^[A-Za-z0-9]{10,16}$/.test(value)
      ? ""
      : "Code must be a 10-16 character alphanumeric string";
  }

  validateMFADeviceName(value: string) {
    const msg =
      "Device name must be 1-64 characters long, cannot start or end with spaces, and may only contain letters, numbers, spaces, and _ . ' ( ) -";

    if (value.length < 1 || value.length > 64) {
      return msg;
    }

    if (value !== value.trim()) {
      return msg;
    }

    // Allow:
    // - Unicode letters/numbers
    // - spaces
    // - _ . ' ( ) -
    const validNameRegex = /^[\p{L}\p{N} _.'()-]+$/u;

    return validNameRegex.test(value) ? "" : msg;
  }
}

export const validators = new Validators();
